<template>
  <b-container fluid>
    <s-empty-list
      v-if="showEmptyList"
      :empty-message="$t('messages.MA076.message')"
      :loading="loading"
    />
    <div
      v-else
      class="chromatography-tab"
    >
      <div class="chromatography-tab__checkbox-area mb-2">
        <b-checkbox
          :checked="areAllSelected"
          @change="toggleAllDiagnostics($event)"
        />
        <span>{{ $t(markAllTitleHandler) }}</span>
      </div>
      <div class="chromatography-tab__diagnostics-wrapper">
        <div
          v-for="(norm, index) in items.norm"
          :key="index"
        >
          <accordion-panel
            class="chromatography-tab__norm-accordion"
            :id="`${norm.norm}-${index}`"
            :collapsed.sync="norm.collapsed"
            >
            <b-row
              class="pl-3"
              slot="title"
            >
              <b-checkbox
                :checked="isDiagnosticsFromNormSelected(norm)"
                @change="toggleAllMethodsFromNorm($event, norm)"
              >
                {{ ruleTitleHandler(norm) }}
              </b-checkbox>
            </b-row>
            <div
              v-for="method in norm.methods"
              :key="method.method"
            >
              <accordion-panel
                class="mx-3 chromatography-tab__accordion-panel"
                :id="method.method"
                :collapsed.sync="method.collapsed"
              >
                <b-row
                  class="pl-3"
                  slot="title"
                >
                  <b-checkbox
                    :checked="isDiagnosticsFromMethodSelected(method)"
                    @change="toggleAll($event, method)"
                  />
                  <strong>
                    {{ methodTitleHandler(norm, method) }}
                  </strong>
                </b-row>
                <b-checkbox-group
                  class="chromatography-tab__checkbox-group ml-3"
                  v-model="selectedDiagnosticsProxy"
                  text-field="name"
                  value-field="id"
                  stacked
                  :options="method.diagnostics"
                />
              </accordion-panel>
            </div>
          </accordion-panel>
        </div>
      </div>
    </div>
  </b-container>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import AccordionPanel from '@/components/AccordionPanel';

export default {
  components: {
    AccordionPanel,
  },
  props: {
    selectedDiagnostics: {
      type: Array,
      default: () => ([]),
    },
    items: Object,
    loading: Boolean,
  },
  computed: {
    selectedDiagnosticsProxy: {
      get() {
        return this.selectedDiagnostics;
      },
      set(value) {
        this.$emit('update:selected-diagnostics', value);
      },
    },
    showEmptyList() {
      return isEmpty(this.items);
    },
    markAllTitleHandler() {
      return this.areAllSelected ? 'reports.diagnostics-history.unmark-all' : 'reports.diagnostics-history.mark-all';
    },
    areAllSelected() {
      if (this.showEmptyList) {
        return false;
      }
      const diagnostics = [];
      this.items.norm
        .forEach(norm => norm.methods
          .forEach(method => method.diagnostics
            .forEach(diagnostic => diagnostics.push(diagnostic.id))));
      return diagnostics.every(item => this.selectedDiagnostics.includes(item));
    },
  },
  methods: {
    ruleTitleHandler({ norm }) {
      return this.$t(`enums.engineering.chromatography.rules.${norm}`);
    },
    methodTitleHandler({ norm }, { method }) {
      return this.$t(`enums.engineering.chromatography.methods.${norm}.${method}`);
    },
    isDiagnosticsFromMethodSelected(method) {
      return method.diagnostics.every(item => this.selectedDiagnostics.includes(item.id));
    },
    isDiagnosticsFromNormSelected(norm) {
      return norm.methods.every(method => method.diagnostics
        .every(diagnostic => this.selectedDiagnostics
          .includes(diagnostic.id)));
    },
    toggleAllDiagnostics(event) {
      if (this.showEmptyList) {
        return;
      }
      if (event) {
        this.items.norm
          .forEach(norm => norm.methods
            .forEach(method => method.diagnostics
              .forEach(diagnostic => this.addSelectedItem(diagnostic.id))));
        return;
      }
      this.items.norm
        .forEach(norm => norm.methods
          .forEach(method => method.diagnostics
            .forEach(diagnostic => this.removeSelectedItem(diagnostic.id))));
    },
    toggleAllMethodsFromNorm(event, norm = []) {
      if (event) {
        norm.methods
          .forEach(method => method.diagnostics
            .forEach(diagnostic => this.addSelectedItem(diagnostic.id)));
        return;
      }
      norm.methods
        .forEach(method => method.diagnostics
          .forEach(diagnostic => this.removeSelectedItem(diagnostic.id)));
    },
    toggleAll(event, norm = []) {
      if (event) {
        norm.diagnostics.forEach(it => this.addSelectedItem(it.id));
        return;
      }
      norm.diagnostics.forEach(it => this.removeSelectedItem(it.id));
    },
    addSelectedItem(selectedItem) {
      const itemExists = this.selectedDiagnostics.some(
        selectedDiagnostic => selectedDiagnostic === selectedItem,
      );
      if (itemExists) return;
      this.selectedDiagnosticsProxy.push(selectedItem);
    },
    removeSelectedItem(selectedItem) {
      const itemIndex = this.selectedDiagnostics.findIndex(
        selectedDiagnostic => selectedDiagnostic === selectedItem,
      );
      this.selectedDiagnosticsProxy.splice(itemIndex, 1);
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables.sass"
  .chromatography-tab
    &__accordion-panel
      max-width: 99%
    &__diagnostics-wrapper
      position: relative
      border: 1px solid $color-border-gray
      border-radius: 5px
      max-height: 300px
      overflow-y: auto
      overflow-x: hidden
    &__checkbox-area
      display: flex
      flex-wrap: nowrap
      margin-bottom: 3px
    &__checkbox-group ::v-deep
      .custom-control.custom-checkbox
        margin-bottom: 5px
    ::v-deep .accordion-panel
      &__slot-container
        margin-right: 0px !important
        margin-top: 0px !important
        .custom-control-label
          font-weight: normal
      &__header
        margin-bottom: 2px
        padding: 4px 10px !important
    &__norm-accordion >
      ::v-deep
        .accordion-panel
          &__header
            background-color: var(--color-table-header-bg)


</style>
